import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../contexts/AppContext';
import { fetchMinneContent } from '../services/Api';
import { getReadablePalette, ReadablePalette } from '../common/Color';
import { formatDate } from '../common/Date';
import { getMinneAnswer, getMinneText, MinneCountry, minneTextToParagraphs, parseMinneDate } from '../common/Minne';
import { u } from '../common/Units';
import Image from './Image';
import Loader from './Loader';
import { LightboxItem, LightboxWrapper } from './LightboxModal';
import { ModalCloseButton, ModalOverlay, modalScrollbar, modalSlideIn, useEscapeKey, useModalOpenClass } from './Modal';
import Avatar from './Avatar';
import CloseIcon from '../assets/icon_close.svg?react';
import MinnerLogo from '../assets/logo_minner.svg?react';
import MinnenLogo from '../assets/logo_minnen.svg?react';

interface MinneModalProps {
  /** Response ID (uuid) */
  uuid: string,
  country: MinneCountry,
  /** Data from the list, shown while the full response loads */
  preview?: any,
  onClose: () => void
}

const Dialog = styled.div<{ $palette: ReadablePalette }>`
  position: relative;
  width: calc(100% - ${u(130)});
  max-width: ${u(1000)};
  max-height: calc(100% - ${u(130)});
  min-height: ${u(300)};
  overflow-y: auto;
  padding: ${u(56)} ${u(64)} ${u(40)};
  box-sizing: border-box;
  border-radius: ${u(24)};
  background-color: ${p => p.$palette.surfaceColor};
  color: ${p => p.$palette.textColor};
  box-shadow: 0 ${u(20)} ${u(60)} rgba(0, 0, 0, 0.4);
  animation: ${modalSlideIn} 0.25s ease-out;
  ${modalScrollbar}

  /* The bar runs along the straight edge, stopping where the rounded corners begin */
  &::-webkit-scrollbar-track {
    margin: ${u(24)} 0;
  }
`;

const Eyebrow = styled.p<{ $palette: ReadablePalette }>`
  margin: 0 ${u(60)} ${u(20)} 0;
  font-size: 0.8em;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.$palette.mutedTextColor};
`;

const Title = styled.h2`
  margin: 0 ${u(60)} ${u(24)} 0;
  font-size: 1.8em;
  line-height: 1.2;
`;

const Contributor = styled.div<{ $palette: ReadablePalette }>`
  display: flex;
  align-items: center;
  gap: ${u(16)};
  margin-bottom: ${u(36)};

  & strong {
    display: block;
    font-weight: 500;
  }

  & span {
    font-size: 0.9em;
    color: ${p => p.$palette.mutedTextColor};
  }
`;

const Images = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$columns}, minmax(0, 1fr));
  gap: ${u(12)};
  margin-bottom: ${u(36)};

  & figure {
    width: 100%;
    height: ${p => p.$columns > 1 ? u(260) : u(440)};
    border-radius: ${u(16)};
    cursor: zoom-in;
  }

  /* With an odd number of images, the first one is shown large so the grid has no gap */
  &.odd > :first-child {
    grid-column: 1 / -1;

    & figure {
      height: ${u(440)};
    }
  }
`;

const Answer = styled.section<{ $palette: ReadablePalette }>`
  & + & {
    margin-top: ${u(32)};
  }

  & h3 {
    margin: 0 0 ${u(10)};
    font-size: 0.95em;
    color: ${p => p.$palette.mutedTextColor};
  }

  & p {
    margin: 0 0 ${u(12)};
    font-size: 1.1em;
    line-height: 1.55;
  }
`;

const Footer = styled.footer<{ $palette: ReadablePalette }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${u(24)};
  margin-top: ${u(40)};
  padding-top: ${u(24)};
  border-top: 1px solid ${p => p.$palette.borderColor};
  font-size: 0.85em;
  color: ${p => p.$palette.mutedTextColor};

  & dl {
    display: grid;
    grid-template-columns: auto auto;
    gap: ${u(4)} ${u(20)};
    margin: 0;
  }

  & dd {
    margin: 0;
    color: ${p => p.$palette.textColor};
  }
`;

const Source = styled.div<{ $palette: ReadablePalette }>`
  text-align: right;

  & svg {
    display: block;
    margin: ${u(8)} 0 0 auto;
    height: ${u(26)};
    width: auto;
  }

  & svg path {
    fill: ${p => p.$palette.textColor};
  }
`;

/**
 * An image that opens in the lightbox. The lightbox needs the resolved media (with url), which the image reports
 * @returns {JSX.Element} Component template
 */
const MinneImage: FC<{ image: any, objectFit: string }> = ({ image, objectFit }) => {
  const [media, setMedia] = useState<any>(null);
  return (
    <LightboxItem media={media}>
      <div><Image media={image} setMedia={setMedia} objectFit={objectFit}/></div>
    </LightboxItem>
  );
}

/**
 * A response from Minne in a popup: topic, contributor and date, images, and each question with its answer
 * @returns {JSX.Element} Component template
 */
const MinneModal: FC<MinneModalProps> = ({ uuid, country, preview, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentLanguage } = useContext(AppContext);
  const [response, setResponse] = useState<any>(null);
  const palette = useMemo(() => getReadablePalette(theme?.backgroundColor), [theme?.backgroundColor]);
  useModalOpenClass();

  useEffect(() => {
    let isActive = true;
    setResponse(null);
    fetchMinneContent(`response/${uuid}`, country).then(data => {
      isActive && setResponse(data);
    }).catch(() => console.error("Could not fetch Minne response"));
    return () => { isActive = false; };
  }, [uuid, country]);

  useEscapeKey(onClose);

  const data = response || preview;
  const topic = getMinneText(data?.topic?.name_i18n, currentLanguage) || data?.topic?.name || "";
  const title = getMinneText(data?.title_i18n, currentLanguage) || data?.title || "";
  const name: string = data?.contributor?.display_name || "";
  const date = parseMinneDate(data?.created);
  const media: any[] = response?.media || [];

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <Dialog $palette={palette} className="scroll" role="dialog" aria-modal="true" aria-label={title || topic} onClick={e => e.stopPropagation()}>
        <ModalCloseButton $palette={palette} onClick={onClose} aria-label={t("dm.close")}><CloseIcon/></ModalCloseButton>

        {topic && <Eyebrow $palette={palette}>{topic}</Eyebrow>}
        {title && <Title>{title}</Title>}

        <Contributor $palette={palette}>
          <Avatar name={name}/>
          <div>
            {name && <strong>{name}</strong>}
            {!isNaN(date) && <span>{formatDate(date)}</span>}
          </div>
        </Contributor>

        {media.length > 0 && (
          <LightboxWrapper>
            <Images $columns={media.length > 1 ? 2 : 1} className={media.length > 1 && media.length % 2 ? "odd" : undefined}>
              {media.map((item: any, i: number) => {
                const image = { dmsId: item?.dms_id, media_type: "image", title: item?.description };
                return <MinneImage key={i} image={image} objectFit={media.length > 1 ? "cover" : "contain"}/>;
              })}
            </Images>
          </LightboxWrapper>
        )}

        {response ? (response.values || []).map((value: any, i: number) => {
          const paragraphs = getMinneAnswer(value, currentLanguage);
          if (paragraphs.length === 0) { return null; }
          const question = getMinneText(value?.topic_item?.label_i18n, currentLanguage) || value?.topic_item?.label;
          return (
            <Answer key={value?.uuid || i} $palette={palette}>
              {question && <h3>{minneTextToParagraphs(question).join(" ")}</h3>}
              {paragraphs.map((text, j) => <p key={j}>{text}</p>)}
            </Answer>
          );
        }) : <Loader variant="inline"/>}

        {response && (
          <Footer $palette={palette}>
            <dl>
              {response.archive_code && <><dt>{t("minne.archiveCode")}</dt><dd>{response.archive_code}</dd></>}
              {response.license && <><dt>{t("minne.license")}</dt><dd>{response.license}</dd></>}
            </dl>
            <Source $palette={palette}>
              {t("minne.contentFrom")}
              {country === "sv" ? <MinnenLogo aria-label="minnen.se"/> : <MinnerLogo aria-label="minner.no"/>}
            </Source>
          </Footer>
        )}
      </Dialog>
    </ModalOverlay>,
    document.body
  );
}

export default MinneModal;
